const GAME_IDS = [
  'memory', 'puzzle', 'logic', 'pattern', 'speed', 'dino', 'snake', 'tetris',
  'game2048', 'colormatch', 'wordsearch', 'math', 'reaction', 'rhythm', 'maze',
];

const STATS_KEY = 'gamesHubStats';
const VISIT_KEY = 'gamesHubVisits';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function loadStats() {
  try {
    return JSON.parse(localStorage.getItem(STATS_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveStats(stats) {
  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
}

function loadVisits() {
  try {
    return JSON.parse(localStorage.getItem(VISIT_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveVisits(dates) {
  localStorage.setItem(VISIT_KEY, JSON.stringify(dates.slice(0, 60)));
}

export function hashDate(dateStr = todayKey()) {
  let h = 0;
  for (let i = 0; i < dateStr.length; i++) {
    h = (h * 31 + dateStr.charCodeAt(i)) >>> 0;
  }
  return h;
}

export function getDailyChallengeId(dateStr = todayKey()) {
  const idx = hashDate(dateStr) % GAME_IDS.length;
  return GAME_IDS[idx];
}

export function recordVisit() {
  const dates = loadVisits();
  const today = todayKey();
  if (!dates.includes(today)) {
    saveVisits([today, ...dates]);
  }
}

export function recordGamePlay(gameId) {
  if (!gameId) return;
  const stats = loadStats();
  const entry = stats[gameId] || { plays: 0, bestScore: 0, lastPlayed: 0 };
  entry.plays += 1;
  entry.lastPlayed = Date.now();
  stats[gameId] = entry;
  const playedSet = new Set(Object.keys(stats).filter((id) => stats[id].plays > 0));
  stats.__playedGames = [...playedSet];
  saveStats(stats);
  checkAchievements(stats);
}

export function recordGameScore(gameId, score) {
  if (!gameId || typeof score !== 'number') return;
  const stats = loadStats();
  const entry = stats[gameId] || { plays: 0, bestScore: 0, lastPlayed: 0 };
  entry.bestScore = Math.max(entry.bestScore || 0, score);
  entry.lastScore = score;
  entry.lastPlayed = Date.now();
  stats[gameId] = entry;
  saveStats(stats);
}

export function getGameStats(gameId) {
  const stats = loadStats();
  return stats[gameId] || { plays: 0, bestScore: 0, lastPlayed: 0 };
}

export function getPlayedGameCount() {
  const stats = loadStats();
  return (stats.__playedGames || []).length;
}

export function getStreakDays() {
  const dates = [...new Set(loadVisits())].sort().reverse();
  if (dates.length === 0) return 0;
  let streak = 0;
  const cursor = new Date(todayKey());
  for (let i = 0; i < dates.length; i++) {
    const expected = cursor.toISOString().slice(0, 10);
    if (dates.includes(expected)) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else if (i === 0 && dates[0] !== expected) {
      break;
    } else {
      break;
    }
  }
  return streak;
}

const ACHIEVEMENT_DEFS = [
  { id: 'first_play', test: (s) => (s.__playedGames || []).length >= 1 },
  { id: 'played_3', test: (s) => (s.__playedGames || []).length >= 3 },
  { id: 'played_5', test: (s) => (s.__playedGames || []).length >= 5 },
  { id: 'played_10', test: (s) => (s.__playedGames || []).length >= 10 },
  { id: 'streak_3', test: () => getStreakDays() >= 3 },
];

export function getAchievements() {
  const stats = loadStats();
  const unlocked = stats.__achievements || [];
  return ACHIEVEMENT_DEFS.map((def) => ({
    id: def.id,
    unlocked: unlocked.includes(def.id) || def.test(stats),
  }));
}

function checkAchievements(stats) {
  const unlocked = new Set(stats.__achievements || []);
  for (const def of ACHIEVEMENT_DEFS) {
    if (def.test(stats)) unlocked.add(def.id);
  }
  stats.__achievements = [...unlocked];
  saveStats(stats);
}

export function setupGameStatsBridge(gameId) {
  recordVisit();
  recordGamePlay(gameId);
  window.gamesHubStats = {
    recordScore: (score) => recordGameScore(gameId, score),
    recordComplete: () => recordGamePlay(gameId),
  };
  return () => {
    delete window.gamesHubStats;
  };
}

export function listenGameScoreEvents(gameId) {
  const handler = (e) => {
    const detail = e.detail || {};
    if (detail.gameId && detail.gameId !== gameId) return;
    if (typeof detail.score === 'number') recordGameScore(gameId, detail.score);
    else recordGamePlay(gameId);
  };
  window.addEventListener('gameshub:score', handler);
  return () => window.removeEventListener('gameshub:score', handler);
}

export { GAME_IDS };
